import { createContext, ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { setMonitoringUser } from '@med/helpers/monitoring'

import Loader from '@/components/Loader'
import { clearApiCache, clearOfflineState } from '@/offline/clearOfflineState'
import { removeQueuedMutationsForOtherOwners } from '@/offline/mutationQueue'
import { api } from '@/services'
import { useLazyGetDriverProfileQuery, useLogoutMutation } from '@/services/Auth/AuthService'
import type { DriverProfileDto } from '@/services/Auth/AuthService.dto'
import { useAppDispatch } from '@/store'
import { commitDriverProfile } from '@/store/reducers/DriverSlice'
import {
  AUTH_STORAGE_KEYS,
  clearAuthStorage,
  get,
  setAuthStorage,
} from '@/utils/storage/adapters/localStorageAdapter'

interface LoginPayload {
  token: string
  tokenExpiresAt: string
  refreshToken: string
  refreshTokenExpiresAt: string
}

interface AuthContextType {
  isLoggedIn: boolean
  isLoading: boolean
  authorize: () => Promise<void>
  login: (payload: LoginPayload) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface JWTProviderProps {
  children: ReactNode
}

const isDateExpired = (value?: string | null): boolean => {
  if (!value) return true

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return true

  return Date.now() >= date.getTime()
}

export const JWTProvider = ({ children }: JWTProviderProps) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [getDriverProfile] = useLazyGetDriverProfileQuery()
  const [triggerLogout] = useLogoutMutation()

  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [isLoading, setIsLoading] = useState(() => {
    const token = get(AUTH_STORAGE_KEYS.token)
    const refreshToken = get(AUTH_STORAGE_KEYS.refreshToken)
    return Boolean(token && refreshToken)
  })

  const applyProfile = useCallback(async (): Promise<DriverProfileDto> => {
    const profile = await getDriverProfile().unwrap()
    dispatch(commitDriverProfile(profile))
    setMonitoringUser({ id: profile.phoneNumber, username: profile.fullName })
    return profile
  }, [dispatch, getDriverProfile])

  const authorize = useCallback(async () => {
    const token = get(AUTH_STORAGE_KEYS.token)
    const refreshToken = get(AUTH_STORAGE_KEYS.refreshToken)
    const refreshTokenExpiresAt = get(AUTH_STORAGE_KEYS.refreshTokenExpiresAt)

    if (!token || !refreshToken) {
      setIsLoggedIn(false)
      setIsLoading(false)
      return
    }

    if (isDateExpired(refreshTokenExpiresAt)) {
      clearAuthStorage()
      dispatch(commitDriverProfile({ fullName: '', phoneNumber: '' }))
      setIsLoggedIn(false)
      setIsLoading(false)
      return
    }

    try {
      await applyProfile()
      setIsLoggedIn(true)
    } catch (error) {
      window.console.error(error, 'authorize-error')
      clearAuthStorage()
      dispatch(commitDriverProfile({ fullName: '', phoneNumber: '' }))
      setIsLoggedIn(false)
    } finally {
      setIsLoading(false)
    }
  }, [applyProfile, dispatch])

  useEffect(() => {
    authorize()
  }, [authorize])

  const login = useCallback(
    async (payload: LoginPayload) => {
      dispatch(api.util.resetApiState())
      await clearApiCache()
      setAuthStorage(payload)
      try {
        const profile = await applyProfile()
        await removeQueuedMutationsForOtherOwners(profile.phoneNumber)
        setIsLoggedIn(true)
        navigate('/home')
      } catch (error) {
        clearAuthStorage()
        dispatch(commitDriverProfile({ fullName: '', phoneNumber: '' }))
        setIsLoggedIn(false)
        throw error
      }
    },
    [applyProfile, dispatch, navigate]
  )

  const logout = useCallback(async () => {
    try {
      await triggerLogout().unwrap()
    } catch (error) {
      window.console.error(error, 'logout-error')
    }
    dispatch(api.util.resetApiState())
    try {
      await clearOfflineState()
    } catch (error) {
      window.console.error(error, 'offline-state-clear-error')
    }
    clearAuthStorage()
    dispatch(commitDriverProfile({ fullName: '', phoneNumber: '' }))
    setMonitoringUser(null)
    setIsLoggedIn(false)
    navigate('/login', { replace: true })
  }, [dispatch, navigate, triggerLogout])

  const value = useMemo(
    () => ({
      isLoggedIn,
      isLoading,
      authorize,
      login,
      logout,
    }),
    [isLoggedIn, isLoading, authorize, login, logout]
  )

  return (
    <AuthContext.Provider value={value}>{isLoading ? <Loader /> : children}</AuthContext.Provider>
  )
}

export default AuthContext
